/* ============================================================
   DELIVERY ESTIMATOR — Core Engine
   Gordon Jewelers | Stiletto Theme
   ============================================================
   Single engine, multiple entry points.
   - PDP block / custom PDP: mode = 'pdp'
   - Cart line item:          mode = 'cart'
   ============================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────
     CONFIG
     ────────────────────────────────────────────── */
  const CONFIG = {
    leadTimes: {
      'stock item': 1,
      'vendor order': 5,
      'made to order': 16,
      'custom': 29,
      'vendor discontinued': -1, // handled by inventory resolution
      '_default': 4              // fallback when no lead time is set
    },

    // Zero stock with Continue selling on but a "stock item" lead time (0 or 1 day)
    // means the lead-time metafields are stale. Use this instead.
    outOfStockFallbackDays: 14,

    shippingCutoffHour: 14,      // 2 PM CST, last carrier handoff
    pickupCutoffBufferMin: 30,   // same-day pickup ends this many minutes before close
    timezone: 'America/Chicago', // CST/CDT

    // Store hours by weekday (0 = Sun), [open, close] in 24h. null = closed.
    // Single source of truth for pickup days. The PDP callouts read this too.
    storeHours: {
      0: null,
      1: null,
      2: [10, 18],
      3: [10, 18],
      4: [10, 18],
      5: [10, 18],
      6: [10, 15]
    },

    standardTransit: 3,          // business days
    overnightTransit: 1,         // business days

    overnightCost: 3500,         // $35.00 in cents
    standardCost: 1200,          // $12.00 in cents
    freeShippingThreshold: 20000,    // $200.00 — standard becomes free
    freeOvernightThreshold: 100000,  // $1,000.00 — overnight becomes free

    shippingSkipDays: [0, 6],    // Sun, Sat
    handoffSkipDays: [0, 1, 6],  // Sun, Mon, Sat — store closed + no carrier pickup

    // Vendor orders: placed with the vendor on vendor business days only,
    // and the lead time counts vendor business days only
    vendorCutoffHour: 14,        // 2 PM CST, last vendor order placement for the day
    vendorSkipDays: [0, 6],      // Sun, Sat, vendors closed

    storeName: 'Gordon Jewelers, Boonville, MO',
    contactUrl: '/pages/contact'
  };

  // Pickup skip days come from the hours table so the two can never drift apart
  CONFIG.pickupSkipDays = Object.keys(CONFIG.storeHours)
    .filter(function (d) { return !CONFIG.storeHours[d]; })
    .map(Number);

  /* ──────────────────────────────────────────────
     DATE UTILITIES
     ────────────────────────────────────────────── */

  /** Get current date/time in CST */
  function nowCST() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: CONFIG.timezone }));
  }

  /** Check if a day (0=Sun … 6=Sat) is in the skip list */
  function isSkipDay(date, skipDays) {
    return skipDays.includes(date.getDay());
  }

  /** Add calendar days (all 7 count) */
  function addCalendarDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  /** Add business days, skipping specified days */
  function addBusinessDays(date, days, skipDays) {
    const d = new Date(date);
    let remaining = days;
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      if (!isSkipDay(d, skipDays)) {
        remaining--;
      }
    }
    return d;
  }

  /** Bump a date forward until it lands on a non-skip day */
  function landOnOpenDay(date, skipDays) {
    const d = new Date(date);
    while (isSkipDay(d, skipDays)) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  /** Check if we're past a cutoff hour (in CST) */
  function isPastCutoff(cutoffHour) {
    return nowCST().getHours() >= cutoffHour;
  }

  /** Store hours for a date's weekday, or null when closed */
  function storeHoursFor(date) {
    return CONFIG.storeHours[date.getDay()] || null;
  }

  /** True while the store is open today with time left to pick up */
  function canPickUpToday() {
    var now = nowCST();
    var hours = storeHoursFor(now);
    if (!hours) return false;
    var t = now.getHours() + now.getMinutes() / 60;
    return t < hours[1] - CONFIG.pickupCutoffBufferMin / 60;
  }

  /** Get the effective "start date" after cutoff adjustment */
  function getStartDate(cutoffHour, skipDays) {
    let start = nowCST();
    if (isPastCutoff(cutoffHour)) {
      start.setDate(start.getDate() + 1);
    }
    // If start lands on a skip day, bump forward
    start = landOnOpenDay(start, skipDays);
    return start;
  }

  /** Check if two dates are the same calendar day */
  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  /* ──────────────────────────────────────────────
     FORMAT
     ────────────────────────────────────────────── */

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatDate(date, prefix) {
    const today = nowCST();
    const tomorrow = addCalendarDays(today, 1);

    if (isSameDay(date, today)) {
      return prefix === 'ready' ? 'Ready Today' : 'Arrives Today';
    }
    if (isSameDay(date, tomorrow)) {
      return prefix === 'ready' ? 'Ready Tomorrow' : 'Arrives Tomorrow';
    }

    const dayName = DAY_NAMES[date.getDay()];
    const monthName = MONTH_NAMES[date.getMonth()];
    const dayNum = date.getDate();
    const verb = prefix === 'ready' ? 'Ready' : 'Arrives';

    return verb + ' ' + dayName + ', ' + monthName + ' ' + dayNum;
  }

  function formatCost(cents) {
    if (cents === 0) return 'Free';
    return '$' + (cents / 100).toFixed(2);
  }

  function formatISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  /* ──────────────────────────────────────────────
     AVAILABILITY RESOLUTION
     ────────────────────────────────────────────── */

  /**
   * Determines if a product/variant is sellable and its effective lead time.
   *
   * @param {Object} data
   * @param {string} data.leadTime         - metafield value
   * @param {string} data.inventoryManagement - 'shopify' | '' | null
   * @param {string} data.inventoryPolicy     - 'continue' | 'deny'
   * @param {number} data.inventoryQuantity
   *
   * @returns {{ available: boolean, effectiveLeadDays: number, reason: string }}
   */
  
  function resolveAvailability(data) {
    const raw = (data.leadTime || '').trim().toLowerCase();
    const continuesSelling = data.inventoryPolicy === 'continue';
    const qty = parseInt(data.inventoryQuantity, 10) || 0;
    const hasStock = qty > 0;
    // Studio rings: the Nivoda plugin builds the product on the fly with a positive
    // quantity and no lead time, so the snippets pass forceLeadTime and the
    // quantity tests are skipped. The lead time they pass is the whole story.
    const forceLead = data.forceLeadTime === true;

    // Quantity is what counts; the tracking flag is ignored (Shopify reports the count either way).

    // Test 1: quantity 0 and Continue selling off: not sellable.
    if (!forceLead && !hasStock && !continuesSelling) {
      return { available: false, inStock: false, effectiveLeadDays: 0, reason: 'no stock, continue selling off' };
    }

    // Test 2: quantity above 0 is in the case. Same-day rules, lead time ignored.
    if (!forceLead && hasStock) {
      return { available: true, inStock: true, effectiveLeadDays: 0, reason: 'in stock' };
    }

    // Vendor discontinued with no stock: not sellable, even with Continue selling on.
    if (raw === 'vendor discontinued') {
      return { available: false, inStock: false, effectiveLeadDays: 0, reason: 'discontinued, no stock' };
    }

    // Test 3: quantity 0 with Continue selling on: follow the lead time.
    var leadDays;
    if (data.leadTimeDays != null && !isNaN(data.leadTimeDays)) {
      leadDays = parseInt(data.leadTimeDays, 10);
    } else {
      leadDays = CONFIG.leadTimes[raw];
      if (leadDays === undefined) {
        leadDays = CONFIG.leadTimes['_default'];
      }
    }
    if (leadDays < 0) {
      return { available: false, inStock: false, effectiveLeadDays: 0, reason: 'unknown lead time: ' + data.leadTime };
    }

    var isVendorOrder = raw === 'vendor order';

    // A 0 to 1 day lead at zero quantity is stale stock-item data: nothing ships without stock. Use the fallback.
    // Vendor orders are exempt: a 1 day vendor lead is real, and a 0 means the sync had no number.
    if (isVendorOrder) {
      if (leadDays < 1) leadDays = CONFIG.leadTimes['vendor order'];
    } else if (leadDays <= 1) {
      leadDays = CONFIG.outOfStockFallbackDays;
    }

    return { available: true, inStock: false, effectiveLeadDays: leadDays, vendorOrder: isVendorOrder, reason: raw || 'lead time' };
  }

  /* ──────────────────────────────────────────────
     DATE CALCULATION
     ────────────────────────────────────────────── */

  /**
   * Calculate delivery/ready dates for all three methods.
   *
   * @param {number} leadDays      - effective lead time in calendar days
   * @param {number} cartTotal     - cart total in cents (for shipping cost calc)
   * @param {number} productPrice  - item price in cents (for PDP cost preview)
   *
   * @returns {Object} estimates keyed by method
   */

  function calculateDates(leadDays, cartTotal, productPrice, inStock, vendorOrder) {
    var priceRef = cartTotal != null ? cartTotal : productPrice;
    var today = nowCST();
    // inStock comes from resolveAvailability; older callers that omit it get the old behavior
    if (inStock === undefined) inStock = leadDays <= 1;

    var afterLead;
    if (vendorOrder) {
      // Vendor Order: goes to the vendor today if before cutoff and the vendor is open,
      // otherwise the next vendor business day. Lead time then counts vendor business days only.
      var placeDate = isPastCutoff(CONFIG.vendorCutoffHour) ? addCalendarDays(today, 1) : new Date(today);
      placeDate = landOnOpenDay(placeDate, CONFIG.vendorSkipDays);
      afterLead = addBusinessDays(placeDate, leadDays, CONFIG.vendorSkipDays);
    } else {
      // Made to Order and Custom: calendar days starting from TODAY.
      // Production doesn't stop on weekends or closed days.
      afterLead = addCalendarDays(today, leadDays);
    }

    // ── SHIPPING (Standard + Overnight) ──
    var shipDate;

      if (inStock) {
      // In the case — cutoff determines if we can ship today
      if (isPastCutoff(CONFIG.shippingCutoffHour)) {
        shipDate = addCalendarDays(today, 1);
      } else {
        shipDate = new Date(today);
      }
      // Must land on a day the store is open AND carriers operate (Tue–Fri)
      shipDate = landOnOpenDay(shipDate, CONFIG.handoffSkipDays);
    } else {
      // Non-stock — lead time already passed, land on first valid handoff day
      shipDate = landOnOpenDay(afterLead, CONFIG.handoffSkipDays);
    }

    var standardDate = addBusinessDays(shipDate, CONFIG.standardTransit, CONFIG.shippingSkipDays);
    var standardCost = priceRef >= CONFIG.freeShippingThreshold ? 0 : CONFIG.standardCost;
    var overnightCost = priceRef >= CONFIG.freeOvernightThreshold ? 0 : CONFIG.overnightCost;
    var overnightDate = addBusinessDays(shipDate, CONFIG.overnightTransit, CONFIG.shippingSkipDays);

    // ── PICKUP ──
    var pickupDate;

    if (inStock) {
      // In the case — today while the store is open with time to spare, else the next open day
      pickupDate = canPickUpToday() ? new Date(today) : addCalendarDays(today, 1);
      // Must land on a store-open day (Tue–Sat)
      pickupDate = landOnOpenDay(pickupDate, CONFIG.pickupSkipDays);
    } else {
      // Non-stock — land on first store-open day after lead time
      pickupDate = landOnOpenDay(afterLead, CONFIG.pickupSkipDays);
    }

    return {
      standard: {
        key: 'standard',
        label: 'Standard Delivery',
        carrier: 'USPS Priority',
        date: standardDate,
        dateFormatted: formatDate(standardDate, 'arrives'),
        dateISO: formatISO(standardDate),
        cost: standardCost,
        costFormatted: formatCost(standardCost),
        icon: 'truck'
      },
      overnight: {
        key: 'overnight',
        label: 'Overnight Delivery',
        carrier: 'FedEx Overnight',
        date: overnightDate,
        dateFormatted: formatDate(overnightDate, 'arrives'),
        dateISO: formatISO(overnightDate),
        cost: overnightCost,
        costFormatted: formatCost(overnightCost),
        icon: 'zap'
      },
      pickup: {
        key: 'pickup',
        label: 'In-Store Pickup',
        carrier: CONFIG.storeName,
        date: pickupDate,
        dateFormatted: formatDate(pickupDate, 'ready'),
        dateISO: formatISO(pickupDate),
        cost: 0,
        costFormatted: 'Free',
        icon: 'store'
      }
    };
  }

  /* ──────────────────────────────────────────────
     ICONS (inline SVG)
     ────────────────────────────────────────────── */
  const ICONS = {
    truck: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',

    zap: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',

    store: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',

    info: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',

    check: '<svg class="de-cart-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
  };

  /* ──────────────────────────────────────────────
     PDP RENDERER
     ────────────────────────────────────────────── */

  function renderPDP(container, availability, estimates, selectedKey) {
    if (!availability.available) {
      container.innerHTML =
        '<div class="de-unavailable">' +
        '  <div class="de-unavailable-title">This item is currently unavailable</div>' +
        '  <div class="de-unavailable-body"><a href="' + CONFIG.contactUrl + '">Contact us</a> for special orders</div>' +
        '</div>';
      return;
    }

    const methods = ['standard', 'overnight', 'pickup'];
    let html = '<div class="de-header">Estimated Delivery</div>';
    html += '<ul class="de-options">';

    methods.forEach(function (key) {
      const est = estimates[key];
      const isSelected = key === selectedKey;
      const isToday = est.dateFormatted.indexOf('Today') > -1;
      const costClass = est.cost === 0 ? ' de-cost-free' : '';
      const dateClass = isToday ? ' de-date-today' : '';

      html +=
        '<li class="de-option' + (isSelected ? ' de-selected' : '') + '" data-method="' + key + '">' +
        '  <div class="de-radio"><div class="de-radio-dot"></div></div>' +
        '  <div class="de-option-body">' +
        '    <div class="de-option-row">' +
        '      <span class="de-method-name">' + est.label + '</span>' +
        '      <span class="de-date' + dateClass + '">' + est.dateFormatted + '</span>' +
        '    </div>' +
        '    <div class="de-option-meta">' +
        '      ' + est.carrier + ' · <span class="de-cost' + costClass + '">' + est.costFormatted + '</span>' +
        '    </div>' +
        '  </div>' +
        '</li>';
    });

    html += '</ul>';
    html +=
      '<div class="de-footer">' +
      '  ' + ICONS.info +
      '  <span>Ships from Boonville, MO</span>' +
      '</div>';

    container.innerHTML = html;

    // Bind click events
    container.querySelectorAll('.de-option').forEach(function (el) {
      el.addEventListener('click', function () {
        const method = el.getAttribute('data-method');
        container.querySelectorAll('.de-option').forEach(function (o) {
          o.classList.toggle('de-selected', o.getAttribute('data-method') === method);
        });
        // Store selection on container for Add to Cart hook
        container.setAttribute('data-selected-method', method);
        // Dispatch event for external listeners
        container.dispatchEvent(new CustomEvent('delivery-estimator:select', {
          bubbles: true,
          detail: { method: method, estimate: estimates[method] }
        }));
      });
    });

    container.setAttribute('data-selected-method', selectedKey);
  }

  /* ──────────────────────────────────────────────
     PICKUP DATE HOOK
     See It In Person (and any callout) prints the same pickup date the
     estimator shows: elements with data-gj-pickup-date get "by Tue, Sep 23",
     or "today" / "tomorrow", from the pickup estimate. One source of truth.
     ────────────────────────────────────────────── */

  var LONG_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var LONG_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  function fillPickupDate(est) {
    var nodes = document.querySelectorAll('[data-gj-pickup-date]');
    if (!nodes.length || !est || !est.date) return;
    var today = nowCST();
    var text;
    if (isSameDay(est.date, today)) {
      text = 'today';
    } else if (isSameDay(est.date, addCalendarDays(today, 1))) {
      text = 'tomorrow';
    } else {
      text = 'by ' + LONG_DAY_NAMES[est.date.getDay()] + ', ' +
        LONG_MONTH_NAMES[est.date.getMonth()] + ' ' + est.date.getDate();
    }
    nodes.forEach(function (el) { el.textContent = text; });
  }

  /* ──────────────────────────────────────────────
     CART RENDERER
     ────────────────────────────────────────────── */

  function renderCart(container, availability, estimates, currentKey) {
    if (!availability.available) {
      container.innerHTML =
        '<div class="de-cart-current">' +
        '  <span class="de-cart-method" style="color:var(--red)">Unavailable</span>' +
        '</div>';
      return;
    }

    const est = estimates[currentKey] || estimates.standard;
    const isToday = est.dateFormatted.indexOf('Today') > -1;
    const iconHtml = '<span class="de-cart-icon">' + ICONS[est.icon] + '</span>';

    let html =
      '<div class="de-cart">' +
      '  <div class="de-cart-current">' +
      '    ' + iconHtml +
      '    <span class="de-cart-method">' + est.label + '</span>' +
      '    <span class="de-cart-date">' + (isToday ? '' : ' · ') + est.dateFormatted + '</span>' +
      '    <button class="de-cart-change" type="button">Change</button>' +
      '  </div>' +
      '  <div class="de-cart-dropdown" id="de-dropdown-' + container.id + '">';

    ['standard', 'overnight', 'pickup'].forEach(function (key) {
      const e = estimates[key];
      const isActive = key === currentKey;
      const costClass = e.cost === 0 ? ' de-cart-option-cost-free' : '';

      html +=
        '<div class="de-cart-option' + (isActive ? ' de-cart-active' : '') + '" data-method="' + key + '">' +
        '  <div class="de-cart-option-left">' +
        '    ' + ICONS.check +
        '    <span>' + e.label + '</span>' +
        '    <span class="de-cart-option-cost' + costClass + '">' + e.costFormatted + '</span>' +
        '  </div>' +
        '  <span class="de-cart-option-date">' + e.dateFormatted + '</span>' +
        '</div>';
    });

    html += '  </div></div>';
    container.innerHTML = html;

    // Toggle dropdown
    var changeBtn = container.querySelector('.de-cart-change');
    var dropdown = container.querySelector('.de-cart-dropdown');

    changeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      dropdown.classList.toggle('de-open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function (e) {
      if (!container.contains(e.target)) {
        dropdown.classList.remove('de-open');
      }
    });

    // Option selection
    container.querySelectorAll('.de-cart-option').forEach(function (el) {
      el.addEventListener('click', function () {
        var method = el.getAttribute('data-method');
        var lineItemKey = container.getAttribute('data-line-key');

        // Update Cart API
        updateCartProperties(lineItemKey, method, estimates).then(function () {
          // Re-render with new selection
          renderCart(container, availability, estimates, method);
          // Dispatch event so cart can update totals if needed
          container.dispatchEvent(new CustomEvent('delivery-estimator:cart-update', {
            bubbles: true,
            detail: { lineItemKey: lineItemKey, method: method, estimate: estimates[method] }
          }));
        });
      });
    });
  }

  /* ──────────────────────────────────────────────
     CART API
     ────────────────────────────────────────────── */

  function updateCartProperties(lineItemKey, method, estimates) {
    var est = estimates[method];
    var _variantId = null;
    var _pairId = '';

    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lineQty = 1;
        var mergedProps = {};

        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === lineItemKey) {
            lineQty = cart.items[i].quantity;
            _variantId = cart.items[i].variant_id;
            var existing = cart.items[i].properties || {};
            for (var key in existing) {
              mergedProps[key] = existing[key];
            }
            break;
          }
        }

        _pairId = mergedProps['_pair_id'] || '';
        mergedProps['_delivery_method_key'] = est.key;
        mergedProps['_delivery_method_label'] = est.label;
        mergedProps['_delivery_estimate'] = est.dateISO;

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineItemKey,
            quantity: lineQty,
            properties: mergedProps
          })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (cartData) {
        // Shopify changes the line item key when properties change.
        // Find the updated item and sync all DOM key references.
        if (!cartData.items || !_variantId) return;

        var updatedItem = null;
        for (var j = 0; j < cartData.items.length; j++) {
          var ci = cartData.items[j];
          if (ci.variant_id === _variantId) {
            var ciPair = (ci.properties && ci.properties['_pair_id']) || '';
            if (ciPair === _pairId) {
              updatedItem = ci;
              break;
            }
          }
        }
        if (!updatedItem) return;

        var newKey = updatedItem.key;
        if (newKey === lineItemKey) return; // key didn't change

        // Update delivery estimator container
        var deContainer = document.querySelector('.delivery-estimator[data-line-key="' + lineItemKey + '"]');
        if (deContainer) {
          deContainer.setAttribute('data-line-key', newKey);
          try {
            var config = JSON.parse(deContainer.getAttribute('data-delivery-config'));
            config.lineItemKey = newKey;
            deContainer.setAttribute('data-delivery-config', JSON.stringify(config));
          } catch (e) {}
        }

        // Update cart item and all child key references
        var cartItem = document.querySelector('.cart__form-item[data-key="' + lineItemKey + '"]');
        if (cartItem) {
          cartItem.dataset.key = newKey;
          cartItem.querySelectorAll('.cart-giftwrap-box').forEach(function (box) {
            box.dataset.giftwrapKey = newKey;
          });
          var smaBox = cartItem.querySelector('.cart-sma-box');
          if (smaBox) smaBox.dataset.smaKey = newKey;
        }
      })
      .catch(function (err) {
        console.error('[DeliveryEstimator] Cart update failed:', err);
      });
  }

  /* ──────────────────────────────────────────────
     LINE ITEM PROPERTIES (for Add to Cart)
     ────────────────────────────────────────────── */

  function toLineItemProperties(estimates, selectedKey) {
    var est = estimates[selectedKey];
    return {
      _delivery_method_key: est.key,
      _delivery_method_label: est.label,
      _delivery_estimate: est.dateISO
    };
  }

  /* ──────────────────────────────────────────────
     INIT — read data attributes, calculate, render
     ────────────────────────────────────────────── */

  function initInstance(container) {
    var configStr = container.getAttribute('data-delivery-config');
    if (!configStr) return;

    var data;
    try {
      data = JSON.parse(configStr);
    } catch (e) {
      console.error('[DeliveryEstimator] Invalid config JSON:', e);
      return;
    }

    var availability = resolveAvailability(data);

    if (!availability.available) {
      if (data.mode === 'cart') {
        renderCart(container, availability, {}, null);
      } else {
        renderPDP(container, availability, {}, null);
      }
      return;
    }

    if (data.mode === 'cart') {
      var estimates = calculateDates(
        availability.effectiveLeadDays,
        data.cartTotal != null ? data.cartTotal : null,
        data.productPrice || 0,
        availability.inStock,
        availability.vendorOrder
      );
      var currentMethod = data.currentMethod || 'standard';
      container.setAttribute('data-line-key', data.lineItemKey || '');
      renderCart(container, availability, estimates, currentMethod);
    } else if (data.mode === 'cart-full') {
      var estimates = calculateDates(
        availability.effectiveLeadDays,
        data.cartTotal != null ? data.cartTotal : null,
        data.productPrice || 0,
         availability.inStock,
        availability.vendorOrder
      );
      var currentMethod = data.currentMethod || 'standard';
      if (!data.currentMethod && (data.cartTotal || data.productPrice || 0) >= CONFIG.freeOvernightThreshold) {
        currentMethod = 'overnight';
      }
      container.setAttribute('data-line-key', data.lineItemKey || '');
      renderPDP(container, availability, estimates, currentMethod);
      if (!container._deCartSelectBound) {
        container._deCartSelectBound = true;
        container.addEventListener('delivery-estimator:select', function(e) {
          var method = e.detail.method;
          var lineItemKey = container.getAttribute('data-line-key');
          if (lineItemKey) {
            // Get fresh estimates from the container
            var est = container._deEstimates;
            if (est) updateCartProperties(lineItemKey, method, est);
          }
        });
      }
    } else {
      // PDP mode — account for quantity
      var qtyInput = container.closest('form') 
        ? container.closest('form').querySelector('[name="quantity"]')
        : document.querySelector('[name="quantity"]');
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      var effectivePrice = (data.productPrice || 0) * qty;

      var estimates = calculateDates(
        availability.effectiveLeadDays,
        null,
        effectivePrice,
        availability.inStock,
        availability.vendorOrder
      );

      var defaultMethod = effectivePrice >= CONFIG.freeOvernightThreshold
        ? 'overnight'
        : 'standard';
      renderPDP(container, availability, estimates, defaultMethod);
      fillPickupDate(estimates.pickup);

      // Re-render when quantity changes
      if (qtyInput && !qtyInput._deListenerBound) {
        qtyInput._deListenerBound = true;
        var reRender = function() { initInstance(container); };
        qtyInput.addEventListener('change', reRender);
        qtyInput.addEventListener('input', reRender);

        // Stiletto's +/- buttons set .value programmatically
        // which doesn't fire change/input events — intercept the setter
        var descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        if (descriptor && descriptor.set) {
          var originalSet = descriptor.set;
          Object.defineProperty(qtyInput, 'value', {
            set: function(val) {
              originalSet.call(this, val);
              reRender();
            },
            get: descriptor.get
          });
        }
      }
    }

    // Store estimates on the element for external access
    container._deEstimates = estimates;
    container._deAvailability = availability;

  }

  /* ──────────────────────────────────────────────
     VARIANT CHANGE LISTENER
     ────────────────────────────────────────────── */

  document.addEventListener('delivery-estimator:update', function (e) {
    var detail = e.detail || {};
    // Find the estimator container(s) on the page
    var containers = document.querySelectorAll('.delivery-estimator[data-delivery-config]');
    containers.forEach(function (container) {
      var data = JSON.parse(container.getAttribute('data-delivery-config'));
      // Only update PDP mode containers
      if (data.mode !== 'pdp') return;

      // Merge in new variant data
      data.leadTime = detail.leadTime || data.leadTime;
      data.inventoryQuantity = detail.inventoryQuantity != null ? detail.inventoryQuantity : data.inventoryQuantity;
      data.inventoryPolicy = detail.inventoryPolicy || data.inventoryPolicy;
      data.inventoryManagement = detail.inventoryManagement || data.inventoryManagement;
      data.productPrice = detail.productPrice || data.productPrice;
      data.variantId = detail.variantId || data.variantId;

      // Update the stored config
      container.setAttribute('data-delivery-config', JSON.stringify(data));

      // Re-init
      initInstance(container);
    });
  });

  /* ──────────────────────────────────────────────
     ADD TO CART HOOK
     ────────────────────────────────────────────── */

  /**
   * Call this from your Add to Cart handler to get the properties
   * to inject into the cart form.
   *
   * Usage:
   *   var props = window.DeliveryEstimator.getSelectedProperties();
   *   // Inject props into your form or /cart/add.js payload
   */
  function getSelectedProperties() {
    var container = document.querySelector('.delivery-estimator[data-delivery-config][data-selected-method]');
    if (!container || !container._deEstimates) return null;

    var selectedKey = container.getAttribute('data-selected-method');
    return toLineItemProperties(container._deEstimates, selectedKey);
  }

  /* ──────────────────────────────────────────────
     CART QUANTITY CHANGE LISTENER
     ────────────────────────────────────────────── */

  var _origFetch = window.fetch;
  window.fetch = function(url, options) {
    var result = _origFetch.apply(this, arguments);

    // After any cart change, refresh estimators with new total
    if (typeof url === 'string' &&
      url.indexOf('/cart/update') !== -1
       && options && options.method && options.method.toUpperCase() === 'POST') {
      result.then(function() {
        // Fetch fresh cart total
        _origFetch('/cart.js', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var newTotal = cart.total_price || 0;
          // Update all cart-mode estimators
          document.querySelectorAll('.delivery-estimator[data-delivery-config]').forEach(function(container) {
            var configStr = container.getAttribute('data-delivery-config');
            if (!configStr) return;
            try {
              var data = JSON.parse(configStr);
              if (data.mode !== 'cart-full' && data.mode !== 'cart') return;
              data.cartTotal = newTotal;
              // Preserve the customer's current selection
              var selectedMethod = container.getAttribute('data-selected-method');
              if (selectedMethod) {
                data.currentMethod = selectedMethod;
              }
              container.setAttribute('data-delivery-config', JSON.stringify(data));
              initInstance(container);
            } catch(e) {}
          });
        });
      });
    }

    return result;
  };

  /* ──────────────────────────────────────────────
     AUTO-INIT ON DOM READY
     ────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('.delivery-estimator[data-delivery-config]').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ──────────────────────────────────────────────
     PUBLIC API
     ────────────────────────────────────────────── */

  window.DeliveryEstimator = {
    init: initAll,
    initInstance: initInstance,
    resolveAvailability: resolveAvailability,
    calculateDates: calculateDates,
    getSelectedProperties: getSelectedProperties,
    toLineItemProperties: toLineItemProperties,
    canPickUpToday: canPickUpToday,
    storeHoursFor: storeHoursFor,
    CONFIG: CONFIG
  };

})();
